import { useEffect, useMemo, useState } from "react";

import { PublicSettingsResponse, settingsApi } from "../api/settings.api";
import { useBalanceStore } from "../store/balance.store";

let cachedSettings: PublicSettingsResponse | null = null;
let settingsPromise: Promise<PublicSettingsResponse> | null = null;

const loadSettings = (): Promise<PublicSettingsResponse> => {
  if (cachedSettings) {
    return Promise.resolve(cachedSettings);
  }
  if (!settingsPromise) {
    settingsPromise = settingsApi
      .getPublicSettings()
      .then(settings => {
        cachedSettings = settings;
        return settings;
      })
      .finally(() => {
        settingsPromise = null;
      });
  }
  return settingsPromise;
};

interface UseFreePreviewResult {
  isPreviewMode: boolean;
  previewsLeft: number;
  previewLimitSeconds: number;
  maxFileSizeBytes: number;
  urlMaxMinutes: number;
  settingsLoaded: boolean;
}

export const useFreePreview = (estimatedMinutes: number | null): UseFreePreviewResult => {
  const balance = useBalanceStore(state => state.balance);
  const [settings, setSettings] = useState<PublicSettingsResponse | null>(cachedSettings);

  useEffect(() => {
    let mounted = true;
    loadSettings()
      .then(data => {
        if (mounted) {
          setSettings(data);
        }
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, []);

  const settingsLoaded = settings !== null && balance !== null;

  const isPreviewMode = useMemo(() => {
    if (!settings || !balance) {
      return false;
    }
    if (!settings.freePreviewEnabled) {
      return false;
    }
    if (!balance.previewEligible) {
      return false;
    }
    if (balance.previewsLeft <= 0) {
      return false;
    }
    if (estimatedMinutes !== null && balance.availableMinutes >= estimatedMinutes) {
      return false;
    }
    return true;
  }, [settings, balance, estimatedMinutes]);

  return {
    isPreviewMode,
    previewsLeft: balance?.previewsLeft ?? 0,
    previewLimitSeconds: settings?.freePreviewLimitSeconds ?? 0,
    maxFileSizeBytes: settings?.freePreviewMaxFileSizeBytes ?? 0,
    urlMaxMinutes: settings?.freePreviewUrlMaxMinutes ?? 0,
    settingsLoaded
  };
};
